import React, { useState, useCallback, useEffect } from "react";
import {
  Pin, BarChart3, Newspaper, Rocket, Hash, Clock, CheckCircle,
  MessageCircle, Send, ChevronDown, ChevronUp, Loader, Sparkles,
} from "lucide-react";
import theme from "../../styles/theme";
import { WizardHatIcon, getAnonIdentity, REACTIONS, timeSince } from "../common/Brand";
import { getRegisteredComponent, hasRegisteredComponent } from "../common/ComponentRegistry";
import InteractivePanel from "../common/InteractivePanel";
import {
  fetchComments,
  createComment,
  toggleReaction as apiToggleReaction,
} from "../../api";
import { generateFingerprint } from "../../api/fingerprint";
import SourcesPanel from "./SourcesPanel";

const T = theme.colors;

const TYPE_CONFIG = {
  analysis: { color: T.accent, Icon: BarChart3, label: "ANALYSIS" },
  news: { color: T.blue, Icon: Newspaper, label: "NEWS" },
  update: { color: T.green, Icon: Rocket, label: "UPDATE" },
};

export default function PostCard({ post }) {
  const [localReactions, setLocalReactions] = useState(post.reactions || {});
  const [reacted, setReacted] = useState({});
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState(post.comments || []);
  const [commentsLoaded, setCommentsLoaded] = useState(!!post.comments?.length);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  // Persistent identity
  const myFingerprint = generateFingerprint();
  const myIdentity = getAnonIdentity(myFingerprint);
  const MyIcon = myIdentity.Icon;

  const authorInfo = post.author === "wyzcon" ? null : getAnonIdentity(post.author || "anon");
  const tc = TYPE_CONFIG[post.type] || TYPE_CONFIG.news;
  const TypeIcon = tc.Icon;

  // Resolve interactive component
  const registeredComponent = hasRegisteredComponent(post.component)
    ? getRegisteredComponent(post.component)
    : null;

  // Load comments from API when panel opens
  useEffect(() => {
    if (showComments && !commentsLoaded) {
      setLoadingComments(true);
      fetchComments(post.id)
        .then((data) => { setComments(data); setCommentsLoaded(true); })
        .catch((err) => console.error("Failed to load comments:", err))
        .finally(() => setLoadingComments(false));
    }
  }, [showComments, commentsLoaded, post.id]);

  const handleReaction = useCallback(async (key) => {
    setLocalReactions((prev) => ({
      ...prev,
      [key]: reacted[key] ? Math.max(0, (prev[key] || 1) - 1) : (prev[key] || 0) + 1,
    }));
    setReacted((prev) => ({ ...prev, [key]: !prev[key] }));
    try {
      const result = await apiToggleReaction(post.id, key);
      if (result.counts) setLocalReactions(result.counts);
    } catch (err) {
      console.error("Reaction failed:", err);
    }
  }, [post.id, reacted]);

  const handleComment = useCallback(async () => {
    if (!commentText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const newComment = await createComment(post.id, { text: commentText });
      setComments((prev) => [newComment, ...prev]);
      setCommentText("");
    } catch (err) {
      console.error("Comment failed:", err);
    } finally {
      setSubmitting(false);
    }
  }, [post.id, commentText, submitting]);

  const commentCount = comments.length || post.commentCount || 0;

  return (
    <>
      <article
        className="fade-in"
        style={{
          background: theme.gradients.card,
          border: `1px solid ${post.pinned ? T.purple + "60" : T.border}`,
          borderRadius: 16,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Pinned banner */}
        {post.pinned && (
          <div style={{
            background: `linear-gradient(90deg, ${T.purple}40, transparent)`,
            padding: "6px 16px", display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, color: T.violet, fontFamily: theme.fonts.mono, letterSpacing: 1,
          }}>
            <Pin size={12} /> PINNED — FEATURED ANALYSIS
          </div>
        )}

        {/* Header */}
        <div style={{ padding: "20px 24px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            {/* Author avatar */}
            {post.author === "wyzcon" ? (
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: theme.gradients.hero,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: `2px solid ${T.purple}`,
              }}>
                <WizardHatIcon size={18} color="#fff" />
              </div>
            ) : (
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: T.surface, border: `1px solid ${authorInfo?.color}40`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {authorInfo && <authorInfo.Icon size={18} color={authorInfo.color} />}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                  {post.author === "wyzcon" ? "Wyzcon Intelligence" : authorInfo?.name}
                </span>
                {post.author === "wyzcon" && (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 3,
                    fontSize: 9, padding: "2px 6px", borderRadius: 4,
                    background: T.purple + "30", color: T.violet,
                    fontWeight: 700, fontFamily: theme.fonts.mono,
                  }}>
                    <CheckCircle size={9} /> VERIFIED
                  </span>
                )}
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                fontSize: 11, color: T.textDim,
              }}>
                <Clock size={10} /> {timeSince(post.timestamp)}
              </div>
            </div>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 10, padding: "3px 8px", borderRadius: 4,
              background: tc.color + "20", color: tc.color,
              fontFamily: theme.fonts.mono, fontWeight: 600, letterSpacing: 1,
            }}>
              <TypeIcon size={11} /> {tc.label}
            </span>
          </div>

          {/* Title & Summary */}
          <h3 style={{
            fontSize: 18, fontWeight: 700, color: "#fff",
            margin: "0 0 8px", lineHeight: 1.35,
          }}>
            {post.title}
          </h3>
          <p style={{
            fontSize: 14, color: T.textMuted,
            lineHeight: 1.6, margin: "0 0 12px",
          }}>
            {post.summary}
          </p>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
              {post.tags.map((t) => (
                <span key={t} style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  fontSize: 10, padding: "2px 8px", borderRadius: 20,
                  background: T.surface, color: T.textDim,
                  border: `1px solid ${T.border}`, fontFamily: theme.fonts.mono,
                }}>
                  <Hash size={9} /> {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Interactive document button */}
        {registeredComponent && (
          <div style={{ padding: "0 24px 16px" }}>
            <button
              onClick={() => setPanelOpen(true)}
              style={{
                width: "100%", padding: "16px 20px",
                borderRadius: 12,
                background: "linear-gradient(135deg, #1a0a2e 0%, #0d1a2e 50%, #0a1a1a 100%)",
                border: `1px solid ${T.purple}40`,
                cursor: "pointer",
                display: "flex", alignItems: "center", gap: 14,
                transition: "all 0.25s",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = T.purple + "90";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = `0 4px 20px ${T.purple}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = T.purple + "40";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Animated glow */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                background: `linear-gradient(90deg, transparent, ${T.purple}08, transparent)`,
                animation: "shimmer 3s ease-in-out infinite",
              }} />
              <style>{`@keyframes shimmer { 0%,100% { opacity: 0; } 50% { opacity: 1; } }`}</style>

              {/* Icon */}
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: T.purple + "20",
                border: `1px solid ${T.purple}40`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <Sparkles size={20} color={T.violet} />
              </div>

              {/* Text */}
              <div style={{ flex: 1, textAlign: "left", position: "relative" }}>
                <div style={{
                  fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
                  color: T.violet, fontFamily: theme.fonts.mono, marginBottom: 3,
                }}>
                  Interactive Document
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>
                  {registeredComponent.title}
                </div>
                {registeredComponent.description && (
                  <div style={{ fontSize: 11, color: T.textDim, marginTop: 3 }}>
                    {registeredComponent.description}
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: T.purple + "25",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, position: "relative",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.violet} strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        )}

        {/* HTML content */}
        {post.content && !post.component && (
          <div
            style={{
              padding: "0 24px 16px", color: T.text,
              lineHeight: 1.7, fontSize: 14,
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        )}

        {/* Sources */}
        <SourcesPanel sources={post.sources} />

        {/* Reactions bar */}
        <div style={{
          padding: "12px 24px", borderTop: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        }}>
          {REACTIONS.map((r) => {
            const count = localReactions[r.key] || 0;
            const isActive = reacted[r.key];
            const RIcon = r.icon;
            return (
              <button key={r.key} onClick={() => handleReaction(r.key)} title={r.label}
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "5px 10px", borderRadius: 20,
                  border: `1px solid ${isActive ? r.color + "80" : T.border}`,
                  background: isActive ? r.color + "18" : "transparent",
                  color: isActive ? r.color : T.textMuted,
                  fontSize: 13, cursor: "pointer", transition: "all 0.15s",
                }}
              >
                <RIcon size={14} strokeWidth={isActive ? 2.5 : 1.8} />
                {count > 0 && (
                  <span style={{ fontSize: 11, fontFamily: theme.fonts.mono }}>{count}</span>
                )}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <button onClick={() => setShowComments(!showComments)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "5px 12px", borderRadius: 20,
              border: `1px solid ${showComments ? T.purple + "60" : T.border}`,
              background: showComments ? T.purple + "15" : "transparent",
              color: showComments ? T.violet : T.textMuted,
              fontSize: 12, cursor: "pointer", fontFamily: theme.fonts.mono,
            }}
          >
            <MessageCircle size={14} /> {commentCount}
            {showComments ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>

        {/* Comments Panel */}
        {showComments && (
          <div style={{
            borderTop: `1px solid ${T.border}`, padding: "16px 24px",
            background: T.midnight + "80",
          }}>
            {/* Your identity banner */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", marginBottom: 14, borderRadius: 8,
              background: myIdentity.color + "10", border: `1px solid ${myIdentity.color}20`,
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%",
                background: myIdentity.color + "20", border: `1px solid ${myIdentity.color}40`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <MyIcon size={12} color={myIdentity.color} />
              </div>
              <span style={{ fontSize: 11, color: myIdentity.color, fontWeight: 600 }}>
                Commenting as {myIdentity.name}
              </span>
              <span style={{ fontSize: 10, color: T.textDim, fontFamily: theme.fonts.mono }}>
                (persistent &amp; anonymous)
              </span>
            </div>

            {/* Comment input */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: myIdentity.color + "15", border: `1px solid ${myIdentity.color}30`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <MyIcon size={15} color={myIdentity.color} />
              </div>
              <div style={{ flex: 1, display: "flex", gap: 8 }}>
                <input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleComment()}
                  placeholder="Share your thoughts anonymously..."
                  style={{
                    flex: 1, padding: "9px 14px", borderRadius: 20,
                    border: `1px solid ${T.border}`, background: T.surface,
                    color: T.text, fontSize: 13, outline: "none", fontFamily: "inherit",
                  }}
                />
                <button onClick={handleComment} disabled={submitting || !commentText.trim()}
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "8px 16px", borderRadius: 20,
                    background: T.purple, color: "#fff", border: "none",
                    fontSize: 12, fontWeight: 600,
                    opacity: commentText.trim() && !submitting ? 1 : 0.4, cursor: "pointer",
                  }}
                >
                  <Send size={12} /> {submitting ? "..." : "Post"}
                </button>
              </div>
            </div>

            {/* Loading */}
            {loadingComments && (
              <div style={{ textAlign: "center", padding: 20 }}>
                <Loader size={18} color={T.violet} style={{ animation: "spin 1s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {/* Comments list */}
            {comments.map((c) => {
              const ca = getAnonIdentity(c.author);
              const CIcon = ca.Icon;
              const isYou = c.author === myFingerprint || c.author?.startsWith(myFingerprint?.slice(0, 8));
              return (
                <div key={c.id} style={{
                  display: "flex", gap: 10, marginBottom: 10,
                  padding: "10px 12px", borderRadius: 10,
                  background: isYou ? myIdentity.color + "08" : T.surface + "60",
                  border: isYou ? `1px solid ${myIdentity.color}15` : "1px solid transparent",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: ca.color + "15", border: `1px solid ${ca.color}30`,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <CIcon size={14} color={ca.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: ca.color + "cc" }}>{ca.name}</span>
                      {isYou && (
                        <span style={{
                          fontSize: 9, padding: "1px 5px", borderRadius: 3,
                          background: ca.color + "20", color: ca.color,
                          fontWeight: 700, fontFamily: theme.fonts.mono,
                        }}>YOU</span>
                      )}
                      <span style={{
                        display: "flex", alignItems: "center", gap: 3,
                        fontSize: 10, color: T.textDim,
                      }}>
                        <Clock size={9} /> {c.ts}
                      </span>
                    </div>
                    <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.5 }}>{c.text}</p>
                  </div>
                </div>
              );
            })}

            {/* Empty state */}
            {!loadingComments && comments.length === 0 && (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <p style={{ fontSize: 13, color: T.textDim }}>
                  No comments yet. Be the first to share your thoughts.
                </p>
              </div>
            )}
          </div>
        )}
      </article>

      {/* Interactive slide panel */}
      {registeredComponent && (
        <InteractivePanel
          isOpen={panelOpen}
          onClose={() => setPanelOpen(false)}
          title={registeredComponent.title}
          description={registeredComponent.description}
          Component={registeredComponent.Component}
          postTitle={post.title}
        />
      )}
    </>
  );
}
